<template>
  <el-dialog
    :title="panelTitle"
    :visible.sync="innerVisible"
    width="480px"
    class="notif-dialog"
    append-to-body
    @closed="onClosed"
  >
    <div v-loading="loading" class="notif-body">
      <div v-if="!loading && !items.length" class="notif-empty">
        {{ emptyText }}
      </div>

      <ul v-else class="notif-list">
        <li
          v-for="n in items"
          :key="n.id"
          class="notif-item"
        >
          <span class="notif-avatar">{{ displayName(n).slice(0, 1) }}</span>
          <div class="notif-content">
            <div class="notif-line">
              <span class="notif-name">{{ displayName(n) }}</span>
              <span class="notif-action">{{ actionLabel(n) }}</span>
            </div>
            <!-- 点赞类没有真实内容（preview 是占位文案），不展示 -->
            <div v-if="n.type !== 'LIKE' && n.type !== 'LIKE_OUT' && n.preview" class="notif-preview">
              {{ n.preview }}
            </div>
            <div v-if="n.blockPreview" class="notif-block">
              <FaIcon :icon="blockIcon" :size="11" />
              <span>{{ n.blockPreview }}</span>
            </div>
            <div class="notif-time">{{ formatTime(n.createdAt) }}</div>
          </div>
        </li>
      </ul>
    </div>
  </el-dialog>
</template>

<script>
import { listNotifications, markNotificationsRead } from '@/api/share.js'
import FaIcon from '@/components/common/FaIcon.vue'
import { faClock } from '@fortawesome/free-solid-svg-icons/faClock'
import { faTableColumns } from '@fortawesome/free-solid-svg-icons/faTableColumns'

export default {
  name: 'NotificationPanel',
  components: { FaIcon },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    /** 'COMMENT' 评论（含回复）/ 'LIKE' 点赞 / 'SHARE' 分享授权 */
    type: {
      type: String,
      default: 'COMMENT'
    }
  },
  data() {
    return {
      innerVisible: false,
      loading: false,
      items: [],
      faClock,
      faTableColumns
    }
  },
  computed: {
    /**
     * 「评论」tab 展示好友评论我（COMMENT）+ 好友回复我（REPLY）+ 我评论好友（COMMENT_OUT）
     * + 我回复好友（REPLY_OUT）——即用户与好友之间双向的评论互动。
     * 「点赞」tab 同理包含 LIKE（好友赞我）与 LIKE_OUT（我赞好友）。
     */
    wantedTypes() {
      if (this.type === 'LIKE') return ['LIKE', 'LIKE_OUT']
      if (this.type === 'SHARE') return ['SHARE']
      return ['COMMENT', 'REPLY', 'COMMENT_OUT', 'REPLY_OUT']
    },
    panelTitle() {
      if (this.type === 'LIKE') return '点赞提醒'
      if (this.type === 'SHARE') return '分享提醒'
      return '评论提醒'
    },
    emptyText() {
      if (this.type === 'LIKE') return '还没有点赞相关的提醒'
      if (this.type === 'SHARE') return '还没有分享相关的提醒'
      return '还没有评论相关的提醒'
    },
    /** 关联对象图标：时间块用时钟，工作表用表格 */
    blockIcon() {
      return this.type === 'SHARE' ? faTableColumns : faClock
    }
  },
  watch: {
    visible(v) {
      if (v && !this.innerVisible) {
        this.innerVisible = true
        this.load()
      } else if (!v) {
        this.innerVisible = false
      }
    },
    innerVisible(v) {
      if (!v) this.$emit('update:visible', false)
    }
  },
  methods: {
    /** *_OUT = 我发出的互动（我评论/回复/点赞了好友），此时 actor 是互动对象（好友） */
    isOutgoing(n) {
      return !!n && (n.type === 'COMMENT_OUT' || n.type === 'REPLY_OUT' || n.type === 'LIKE_OUT')
    },
    /** 动作主体：我发出的显示「你」，收到的显示好友昵称 */
    displayName(n) {
      if (this.isOutgoing(n)) return '你'
      return n.actorNickname || n.actorRemark || '好友'
    },
    /** 互动对象昵称（*_OUT 时 actor 就是好友本人） */
    peerName(n) {
      return n.actorNickname || n.actorRemark || '好友'
    },
    /** 每条通知的动作文案：分享 / 回复 / 评论 / 点赞，双向共 7 种 */
    actionLabel(n) {
      if (!n) return ''
      if (n.type === 'SHARE') return '分享了时间表给你'
      if (n.type === 'REPLY') return '回复了你的评论'
      if (n.type === 'REPLY_OUT') return `回复了 ${this.peerName(n)} 的评论`
      if (n.type === 'COMMENT_OUT') return `评论了 ${this.peerName(n)} 的时间块`
      if (n.type === 'LIKE_OUT') return `赞了 ${this.peerName(n)} 的时间块`
      if (n.type === 'LIKE') return '赞了你的时间块'
      return '评论了你的时间块'
    },
    formatTime(ts) {
      if (!ts) return ''
      const d = new Date(ts)
      if (isNaN(d.getTime())) return ''
      const pad = n => String(n).padStart(2, '0')
      return `${d.getMonth() + 1}/${d.getDate()} ${pad(d.getHours())}:${pad(d.getMinutes())}`
    },
    async load() {
      this.loading = true
      try {
        const res = await listNotifications(50)
        const all = (res.data && res.data.items) || []
        this.items = all.filter(n => this.wantedTypes.includes(n.type))
        // 打开即标记该类型已读（「评论」tab 连带把「回复」一起标记）
        if (this.items.length) {
          try {
            await markNotificationsRead({ types: this.wantedTypes })
            this.$emit('read')
          } catch (e) {
            console.warn('[NotificationPanel] 标记已读失败', e)
          }
        }
      } catch (e) {
        console.error('[NotificationPanel] 加载通知失败', e)
        this.items = []
      } finally {
        this.loading = false
      }
    },
    onClosed() {
      this.$emit('close')
    }
  }
}
</script>

<style scoped>
.notif-body {
  min-height: 100px;
}
.notif-empty {
  padding: 28px 8px;
  text-align: center;
  color: var(--lp-text-3, #94a59b);
  font-size: 13px;
}
.notif-list {
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: 340px;
  overflow-y: auto;
}
.notif-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 4px;
  border-bottom: 1px solid var(--lp-border-soft, #eef5f0);
}
.notif-item:last-child {
  border-bottom: none;
}
.notif-avatar {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: var(--lp-primary, #4aa46a);
  border: 2px solid var(--lp-primary-dark, #2f7a4a);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  color: #fff;
  flex-shrink: 0;
}
.notif-content {
  flex: 1;
  min-width: 0;
}
.notif-line {
  font-size: 13px;
  color: var(--lp-text, #26332b);
}
.notif-name {
  font-weight: 600;
}
.notif-action {
  color: var(--lp-text-2, #5f7268);
  margin-left: 4px;
}
.notif-preview {
  margin-top: 4px;
  font-size: 12px;
  line-height: 1.45;
  color: var(--lp-text-2, #5f7268);
  word-break: break-word;
}
.notif-block {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-top: 5px;
  padding: 3px 9px;
  font-size: 12px;
  color: var(--lp-primary-dark, #2f7a4a);
  background: var(--lp-primary-soft, #e9f5ec);
  border-radius: 0.5rem;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.notif-time {
  margin-top: 4px;
  font-size: 11px;
  color: var(--lp-text-3, #94a59b);
}
</style>
